import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { notifyHostObservationRunPoll } from '@/shared/lib/host-polling-bridge';
import { useCalibrationConfig } from '../hooks/use-calibration-config';
import { useCalibrationEnvironments } from '../hooks/use-calibration-environments';
import { useCalibrationRuns } from '../hooks/use-calibration-runs';
import { useObservationRunStatusSync } from '../hooks/use-observation-run-status-sync';
import {
  isHostBannerStatus,
  isInProgressStatus,
  matchesStatusFilter,
  RUN_STATUS_FILTERS,
} from '../lib/status';
import type { CalibrationRun } from '../lib/types';
import { RunListTable } from './run-list-table';
import { RunListToolbar } from './run-list-toolbar';

type FilterOption = { value: string; label: string };

const STATUS_OPTIONS: FilterOption[] = [
  { value: RUN_STATUS_FILTERS.SCHEDULED, label: 'Scheduled' },
  { value: RUN_STATUS_FILTERS.IN_PROGRESS, label: 'In Progress' },
  { value: RUN_STATUS_FILTERS.COMPLETED, label: 'Completed' },
  { value: RUN_STATUS_FILTERS.FAILED, label: 'Failed' },
  { value: RUN_STATUS_FILTERS.CANCELLED, label: 'Canceled' },
];

function uniqueOwners(runs: CalibrationRun[]): FilterOption[] {
  const seen = new Set<string>();
  const result: FilterOption[] = [];
  for (const r of runs) {
    const id = r.createdBy?.id;
    if (id && !seen.has(id)) {
      seen.add(id);
      result.push({ value: id, label: r.createdBy?.name ?? id });
    }
  }
  return result.sort((a, b) => a.label.localeCompare(b.label));
}

function toggle(prev: string[], value: string): string[] {
  return prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value];
}

export function RunListPage() {
  const [search, setSearch] = useState('');
  const [environments, setEnvironments] = useState<string[]>([]);
  const [owners, setOwners] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);

  const { calibrationConfig } = useCalibrationConfig();
  const { data, isLoading, isError, error } = useCalibrationRuns();
  const runs = useMemo(() => data ?? [], [data]);

  const { environmentOptions: envAxis } = useCalibrationEnvironments();
  // The filter enumerates every pipeline environment (EP) or the
  // Staging/Production pair (non-EP) — names only, no build provenance.
  const environmentOptions = useMemo<FilterOption[]>(
    () => envAxis.map(({ label, value }) => ({ label, value })),
    [envAxis]
  );

  const ownerOptions = useMemo(() => uniqueOwners(runs), [runs]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return runs
      .filter((r) =>
        q ? r.runName.toLowerCase().includes(q) || r.runId.toLowerCase().includes(q) : true
      )
      .filter((r) =>
        environments.length ? environments.includes(r.evaluationEnvironment ?? r.env) : true
      )
      .filter((r) => (owners.length ? owners.includes(r.createdBy?.id ?? '') : true))
      .filter((r) =>
        statuses.length ? statuses.some((s) => matchesStatusFilter(s, r.status)) : true
      )
      .sort((a, b) => Number(b.updatedAt) - Number(a.updatedAt));
  }, [runs, search, environments, owners, statuses]);

  const activeChips = useMemo(() => {
    const chips: { label: string; onRemove: () => void }[] = [];
    for (const v of environments) {
      const label = environmentOptions.find((o) => o.value === v)?.label ?? v;
      chips.push({ label, onRemove: () => setEnvironments((p) => p.filter((e) => e !== v)) });
    }
    for (const v of owners) {
      const label = ownerOptions.find((o) => o.value === v)?.label ?? v;
      chips.push({ label, onRemove: () => setOwners((p) => p.filter((o) => o !== v)) });
    }
    for (const v of statuses) {
      const label = STATUS_OPTIONS.find((o) => o.value === v)?.label ?? v;
      chips.push({ label, onRemove: () => setStatuses((p) => p.filter((s) => s !== v)) });
    }
    return chips;
  }, [environments, owners, statuses, ownerOptions, environmentOptions]);

  const clearAll = useCallback(() => {
    setEnvironments([]);
    setOwners([]);
    setStatuses([]);
  }, []);

  // Mirrors aistudio's `disabledCreateRun`: the backend runs one calibration at
  // a time per bot, and caps total runs, so both conditions block a new run —
  // each with its own explanation. Checked against every run, not the filtered
  // view, since a filter hiding the active run must not re-enable the button.
  const createRunDisabledReason = useMemo(() => {
    if (runs.length >= calibrationConfig.maxRunLimit) {
      return `Run limit reached. Maximum of ${calibrationConfig.maxRunLimit} runs allowed.`;
    }
    // isInProgressStatus covers SCHEDULED and INITIATED too — a queued run
    // occupies the slot just as a running one does.
    if (runs.some((r) => isInProgressStatus(r.status))) {
      return 'A run is already in progress. Please wait until it finishes';
    }
    return undefined;
  }, [runs, calibrationConfig]);

  useEffect(() => {
    if (isError && error) {
      toast.error(error instanceof Error ? error.message : 'Failed to load runs');
    }
  }, [isError, error]);

  const activeRun = useMemo(() => runs.find((r) => isInProgressStatus(r.status)), [runs]);

  // Re-attach the host's polling banner to a run that was already in flight
  // when this list loaded — e.g. the user reloaded, or navigated away and back.
  // Mirrors aistudio's fetchRuns, which emits with showBannerOnCompletion=false
  // so re-attaching doesn't pop a completion banner the user didn't trigger.
  //
  // Only statuses the banner understands are forwarded: a PRE_RUN status, or
  // PREDICTION_COMPLETED, would hit its `default` branch and stop the polling
  // a freshly created run just started. The key carries the status as well as
  // the id, so the same run re-notifies as its in-progress status changes.
  // `activeRun` only ever holds an in-progress status, so this never carries a
  // terminal status — completion itself is detected by the host's own poll,
  // not signalled from here.
  const bannerRun = activeRun && isHostBannerStatus(activeRun.status) ? activeRun : undefined;
  const notifiedRunKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!bannerRun) {
      notifiedRunKeyRef.current = null;
      return;
    }
    const key = `${bannerRun.runId}:${bannerRun.status}`;
    if (notifiedRunKeyRef.current === key) return;
    notifiedRunKeyRef.current = key;
    notifyHostObservationRunPoll({
      showBannerOnCompletion: false,
      run: { id: bannerRun.runId, status: bannerRun.status, runName: bannerRun.runName },
    });
  }, [bannerRun]);

  // Keeps this table's own status/recommendation cells current while
  // `activeRun` runs, by polling the cheap event-status endpoint rather than
  // re-fetching the whole list — see the hook doc for why.
  useObservationRunStatusSync(!!activeRun);

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-2">
        <h1 className="font-semibold text-foreground text-xl leading-none">Calibration</h1>
        <p className="text-base text-muted-foreground">
          Run quality analyses on real conversations and get recommendations to improve performance.
        </p>
      </header>

      <RunListToolbar
        search={search}
        onSearchChange={setSearch}
        environments={environments}
        onEnvironmentToggle={(v) => setEnvironments((p) => toggle(p, v))}
        environmentOptions={environmentOptions}
        owners={owners}
        onOwnerToggle={(v) => setOwners((p) => toggle(p, v))}
        ownerOptions={ownerOptions}
        statuses={statuses}
        onStatusToggle={(v) => setStatuses((p) => toggle(p, v))}
        statusOptions={STATUS_OPTIONS}
        activeChips={activeChips}
        onClearAllFilters={clearAll}
        createRunDisabledReason={createRunDisabledReason}
      />
      <RunListTable
        runs={filtered}
        isLoading={isLoading}
        isError={isError}
        errorMessage={error instanceof Error ? error.message : undefined}
      />
    </div>
  );
}
